/**
 * Models and interfaces for Author Filter Component
 * 
 * @remarks
 * These models define the structure for author selection,
 * including author data, selection state, and configuration.
 */

/**
 * Author information for selection
 * 
 * @remarks
 * Represents a user who can be selected as an author filter.
 * Typically includes Admin and Editor roles who can create/edit posts.
 */
export interface AuthorSelection {
  /** Unique identifier for the author/user */
  id: string;
  
  /** Display name of the author */
  name: string;
  
  /** Email address of the author */
  email: string;
  
  /** Optional: Organization role (Admin, Editor, etc.) */
  role?: string;
  
  /** Optional: Avatar URL */
  avatarUrl?: string;
}

/**
 * Author filter state
 * 
 * @remarks
 * Represents the complete state of the author filter,
 * including loading, error, and selection states.
 */
export interface AuthorFilterState {
  /** List of available authors */
  authors: AuthorSelection[];
  
  /** Currently selected author ID */
  selectedAuthorId: string | null;
  
  /** Whether authors are being loaded */
  loading: boolean;
  
  /** Error message if loading failed */
  error: string | null;
  
  /** Current search term for filtering authors */
  searchTerm: string;
}

/**
 * Configuration options for Author Filter Component
 * 
 * @remarks
 * These options allow customization of the component's behavior and appearance.
 */
export interface AuthorFilterConfig {
  /** Label displayed above the filter */
  label?: string;
  
  /** Placeholder text for the dropdown */
  placeholder?: string;
  
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  
  /** Whether to show the search input */
  showSearch?: boolean;
  
  /** Minimum number of authors before showing search (0 = always show) */
  searchThreshold?: number;
  
  /** Whether to show the clear button */
  showClearButton?: boolean;
  
  /** Whether the filter is disabled */
  disabled?: boolean;
  
  /** Maximum number of authors to load from API */
  maxAuthors?: number;
  
  /** Whether to auto-load authors on init */
  autoLoad?: boolean;
  
  /** Custom empty state message */
  emptyMessage?: string;
  
  /** Custom error message */
  errorMessage?: string;
  
  /** Custom loading message */
  loadingMessage?: string;
}

/**
 * Default configuration values
 */
export const DEFAULT_AUTHOR_FILTER_CONFIG: Required<AuthorFilterConfig> = {
  label: 'Author',
  placeholder: 'All authors',
  searchPlaceholder: 'Search authors...',
  showSearch: true,
  searchThreshold: 5,
  showClearButton: true,
  disabled: false,
  maxAuthors: 50,
  autoLoad: true,
  emptyMessage: 'No authors available',
  errorMessage: 'Unable to load authors',
  loadingMessage: 'Loading authors...'
};

/**
 * Event payload for author change
 * 
 * @remarks
 * Emitted when an author is selected or cleared.
 * Null when author filter is cleared.
 */
export type AuthorChangeEvent = AuthorSelection | null;
